import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm Sparkle ✦ ShoNics' AI concierge. How can I help — instant quote, booking, or service info?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`web-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — connection issue. Please call 943-245-0462 or try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#0B5D3B] text-white shadow-[0_12px_32px_rgba(11,93,59,0.4)] hover:scale-105 transition-all flex items-center justify-center group"
        aria-label="Open chatbot"
        data-testid="chatbot-toggle"
      >
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5 text-[#D4AF37] group-hover:rotate-12 transition-transform" strokeWidth={1.8} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-md h-[70vh] max-h-[600px] bg-white rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.18)] border border-[#D4AF37]/30 overflow-hidden flex flex-col" data-testid="chatbot-panel">
          <div className="bg-[#0B5D3B] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#0B5D3B]" strokeWidth={2} />
            </div>
            <div>
              <div className="font-serif text-lg leading-none">Sparkle</div>
              <div className="text-[11px] text-white/70">ShoNics AI concierge · usually replies instantly</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF7EE]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                  m.role === "user" ? "bg-[#0B5D3B] text-white rounded-br-md" : "bg-white border border-[#EAE3D2] text-[#111] rounded-bl-md"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#EAE3D2] px-4 py-2.5 rounded-2xl text-sm text-[#0B5D3B] flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Sparkle is thinking...
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-[#EAE3D2] p-3 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about a service, get a quote..."
              className="flex-1 px-4 py-2.5 rounded-full border border-[#EAE3D2] focus:outline-none focus:border-[#D4AF37] text-sm"
              data-testid="chatbot-input"
            />
            <button onClick={send} disabled={loading || !input.trim()} className="w-11 h-11 rounded-full bg-[#0B5D3B] text-white flex items-center justify-center disabled:opacity-50" data-testid="chatbot-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
